import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';

export interface WeeklyTarget {
  weekNumber: number;
  revenueTarget: number;
  clientsTarget: number;
  focusAreas: string[];
  actualRevenue?: number;
  actualClients?: number;
}

export interface QuarterlyGoals {
  id?: string;
  barberId: string;
  barberName: string;
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  year: number;

  // Financial Goals
  revenueGoal: number;
  avgTicketGoal: number;
  productSalesGoal: number;

  // Client Goals
  totalClientsGoal: number;
  newClientsGoal: number;
  retentionRateGoal: number;
  rebookRateGoal: number;

  // Weekly Breakdown
  weeklyTargets: WeeklyTarget[];

  // Progress Tracking
  currentRevenue: number;
  currentClients: number;
  currentAvgTicket: number;
  currentRetentionRate: number;

  status: 'draft' | 'active' | 'completed' | 'paused';
  createdBy: string;
  createdAt: Date;
  lastModified: Date;
  notes: string;
}

/**
 * Get quarterly goals for a specific barber, quarter, and year
 */
export const getQuarterlyGoals = async (
  barberId: string,
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4',
  year: number
): Promise<QuarterlyGoals | null> => {
  try {
    const goalsRef = collection(db, 'quarterlyGoals');
    const q = query(
      goalsRef,
      where('barberId', '==', barberId),
      where('quarter', '==', quarter),
      where('year', '==', year)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastModified: data.lastModified?.toDate() || new Date(),
    } as QuarterlyGoals;
  } catch (error) {
    console.error('Error fetching quarterly goals:', error);
    return null;
  }
};

/**
 * Get all quarterly goals for a barber
 */
export const getAllBarberGoals = async (barberId: string): Promise<QuarterlyGoals[]> => {
  try {
    const goalsRef = collection(db, 'quarterlyGoals');
    const q = query(goalsRef, where('barberId', '==', barberId));

    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      lastModified: doc.data().lastModified?.toDate() || new Date(),
    })) as QuarterlyGoals[];
  } catch (error) {
    console.error('Error fetching all barber goals:', error);
    return [];
  }
};

/**
 * Save or update quarterly goals
 */
export const saveQuarterlyGoals = async (
  goals: QuarterlyGoals,
  createdBy: string
): Promise<string> => {
  try {
    const goalsRef = collection(db, 'quarterlyGoals');

    if (goals.id) {
      // Update existing goals
      const docRef = doc(goalsRef, goals.id);
      await updateDoc(docRef, {
        ...goals,
        lastModified: Timestamp.now(),
        createdBy,
      });
      return goals.id;
    } else {
      // Create new goals with a composite ID
      const docId = `${goals.barberId}_${goals.quarter}_${goals.year}`;
      const docRef = doc(goalsRef, docId);

      await setDoc(docRef, {
        ...goals,
        createdAt: Timestamp.now(),
        lastModified: Timestamp.now(),
        createdBy,
      });

      return docId;
    }
  } catch (error) {
    console.error('Error saving quarterly goals:', error);
    throw error;
  }
};

/**
 * Update progress for quarterly goals
 */
export const updateGoalsProgress = async (
  goalId: string,
  progress: {
    currentRevenue?: number;
    currentClients?: number;
    currentAvgTicket?: number;
    currentRetentionRate?: number;
  }
): Promise<void> => {
  try {
    const docRef = doc(db, 'quarterlyGoals', goalId);
    await updateDoc(docRef, {
      ...progress,
      lastModified: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating goals progress:', error);
    throw error;
  }
};

/**
 * Generate default quarterly goals based on historical data
 */
export const generateDefaultGoals = (
  barberId: string,
  barberName: string,
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4',
  year: number,
  historicalData: {
    avgMonthlyRevenue: number;
    avgMonthlyClients: number;
    avgTicket: number;
    retentionRate: number;
  }
): QuarterlyGoals => {
  const { avgMonthlyRevenue, avgMonthlyClients, avgTicket, retentionRate } = historicalData;

  // Apply growth targets (15% revenue growth, 10% client growth)
  const quarterlyRevenue = avgMonthlyRevenue * 3 * 1.15;
  const quarterlyClients = avgMonthlyClients * 3 * 1.10;

  // Generate weekly targets (13 weeks per quarter)
  const weeklyTargets: WeeklyTarget[] = Array.from({ length: 13 }, (_, i) => ({
    weekNumber: i + 1,
    revenueTarget: Math.round(quarterlyRevenue / 13),
    clientsTarget: Math.round(quarterlyClients / 13),
    focusAreas: i < 4 ? ['client acquisition'] : i < 8 ? ['retention'] : ['upselling'],
  }));

  return {
    barberId,
    barberName,
    quarter,
    year,
    revenueGoal: Math.round(quarterlyRevenue),
    avgTicketGoal: Math.round(avgTicket * 1.05), // 5% increase
    productSalesGoal: Math.round(quarterlyRevenue * 0.15), // 15% of revenue from products
    totalClientsGoal: Math.round(quarterlyClients),
    newClientsGoal: Math.round(quarterlyClients * 0.20), // 20% new clients
    retentionRateGoal: Math.min(retentionRate + 3, 98), // +3% retention (max 98%)
    rebookRateGoal: 85,
    weeklyTargets,
    currentRevenue: 0,
    currentClients: 0,
    currentAvgTicket: avgTicket,
    currentRetentionRate: retentionRate,
    status: 'draft',
    createdBy: 'system',
    createdAt: new Date(),
    lastModified: new Date(),
    notes: 'Auto-generated quarterly goals based on historical performance',
  };
};
